// src/components/SelectorReferenciaCliente.js
import React, { useState, useEffect, useRef } from 'react';
import { asignarReferenciaCliente, buscarClientes } from '../services/clienteService';
import ComboboxDatosMaestros from './ComboboxDatosMaestros';
import { apellidoNombre } from '../utils/nombrePersona';

// Referencia de catálogo que el backend asigna cuando el cliente fue traído por
// alguien de quien no se puede determinar la referencia de origen. Acá solo se usa
// para anticipar en pantalla lo que va a guardar ClienteService.aplicarReferencia.
const REFERENCIA_OTRO_CLIENTE = 'OTRO CLIENTE';

/**
 * Selector de referencia de un cliente. Tres formas de llegar al estudio:
 *
 *   1. Referencia del catálogo habitual (Facebook, Instagram, un empleado, una
 *      persona ajena al estudio, etc.) -> combobox de datos maestros.
 *   2. Traído por otro CLIENTE del estudio -> se busca y se vincula por CUIL, y la
 *      referencia se hereda de ese cliente (el backend la mantiene sincronizada).
 *   3. Traído por alguien que todavía NO es cliente -> se escribe el nombre a mano.
 *      No hay referencia de origen que heredar, así que queda "OTRO CLIENTE".
 *
 * @param {Object} value - { referencia, idReferencia, cuilClienteReferente, nombreClienteReferente, nombreReferenteExterno }
 * @param {Function} onChange - recibe un objeto parcial para mergear en el estado del cliente
 * @param {Array} referenciasDisponibles - opciones del catálogo para el combobox
 * @param {Function} onCrearNuevaReferencia - callback cuando el combobox crea una referencia nueva
 * @param {string} excluirCuil - CUIL a excluir de los resultados de búsqueda (modo edición)
 * @param {Function} icon - icono para el combobox de catálogo
 */
export default function SelectorReferenciaCliente({
    value,
    onChange,
    referenciasDisponibles = [],
    onCrearNuevaReferencia,
    excluirCuil = null,
    icon,
}) {
    const hayReferente = Boolean(value?.cuilClienteReferente || value?.nombreReferenteExterno);

    const [modoOtroCliente, setModoOtroCliente] = useState(hayReferente);
    const [modoManual, setModoManual] = useState(false);
    const [nombreManual, setNombreManual] = useState('');
    const [criterio, setCriterio] = useState('');
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const debounceRef = useRef(null);
    const MAX_RESULTADOS_VISIBLES = 6;
    // Referente elegido que no tiene referencia del catálogo (texto libre viejo, o ninguna): lo que
    // se hereda es OTRO CLIENTE, salvo que acá se le asigne una. { cuil, nombre, textoLibre }
    const [referenteSinCatalogo, setReferenteSinCatalogo] = useState(null);
    const [idReferenciaParaReferente, setIdReferenciaParaReferente] = useState('');
    const [asignando, setAsignando] = useState(false);
    const [mensajeAsignacion, setMensajeAsignacion] = useState(null); // { tipo: 'ok'|'error', texto }

    useEffect(() => {
        setModoOtroCliente(Boolean(value?.cuilClienteReferente || value?.nombreReferenteExterno));
    }, [value?.cuilClienteReferente, value?.nombreReferenteExterno]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!criterio.trim()) {
            setResultados([]);
            return undefined;
        }
        debounceRef.current = setTimeout(async () => {
            setBuscando(true);
            try {
                const data = await buscarClientes(criterio.trim());
                const lista = Array.isArray(data) ? data : [];
                setResultados(excluirCuil ? lista.filter((c) => c.cuil !== excluirCuil) : lista);
            } catch (e) {
                setResultados([]);
            } finally {
                setBuscando(false);
            }
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [criterio, excluirCuil]);

    const limpiarBusqueda = () => {
        setCriterio('');
        setResultados([]);
        setModoManual(false);
        setNombreManual('');
    };

    const handleToggle = (activar) => {
        setModoOtroCliente(activar);
        limpiarBusqueda();
        if (activar) {
            onChange({ referencia: '', idReferencia: null });
        } else {
            onChange({ cuilClienteReferente: null, nombreClienteReferente: '', nombreReferenteExterno: null });
        }
    };

    const handleSeleccionarReferente = (cliente) => {
        // Solo se hereda una referencia del catálogo (ClienteService.aplicarReferenciaHeredadaDe). Si el
        // referente tiene la suya en texto libre, lo que se guarda es OTRO CLIENTE: se muestra eso, no
        // el texto, y se ofrece asignarle una del catálogo antes de guardar (caso FARIAS, 29-sep-2026).
        const tieneCatalogo = Boolean(cliente.idReferencia);
        const nombre = `${cliente.nombre} ${cliente.apellido}`;
        onChange({
            cuilClienteReferente: cliente.cuil,
            nombreClienteReferente: nombre,
            nombreReferenteExterno: null,
            referencia: tieneCatalogo ? cliente.referencia : REFERENCIA_OTRO_CLIENTE,
            idReferencia: tieneCatalogo ? cliente.idReferencia : null,
        });
        setReferenteSinCatalogo(tieneCatalogo ? null : {
            cuil: cliente.cuil, nombre, textoLibre: (cliente.referencia || '').trim(),
        });
        setIdReferenciaParaReferente('');
        setMensajeAsignacion(null);
        limpiarBusqueda();
    };

    // Le asigna al referente la referencia del catálogo elegida. El backend la guarda en él y la
    // propaga a todos los que trajo; este cliente todavía no existe, así que acá se actualiza la
    // vista previa y al guardarlo hereda la nueva.
    const handleAsignarAlReferente = async () => {
        const elegida = referenciasDisponibles.find((r) => String(r.id) === String(idReferenciaParaReferente));
        if (!elegida || !referenteSinCatalogo) return;
        setAsignando(true);
        setMensajeAsignacion(null);
        try {
            await asignarReferenciaCliente(referenteSinCatalogo.cuil, elegida.id);
            onChange({ referencia: elegida.nombre, idReferencia: elegida.id });
            setMensajeAsignacion({
                tipo: 'ok',
                texto: `Listo: ${referenteSinCatalogo.nombre} quedó con «${elegida.nombre}» y este cliente la hereda.`,
            });
            setReferenteSinCatalogo(null);
        } catch (e) {
            setMensajeAsignacion({
                tipo: 'error',
                texto: e?.response?.data?.message || 'No se pudo asignar la referencia al referente.',
            });
        } finally {
            setAsignando(false);
        }
    };

    // El backend normaliza igual a mayúsculas (ClienteService.normalizarNombreReferenteExterno);
    // acá se hace en vivo para que el usuario vea exactamente lo que se va a guardar.
    const handleConfirmarManual = () => {
        const nombre = nombreManual.trim().replace(/\s+/g, ' ').toUpperCase();
        if (!nombre) return;
        onChange({
            cuilClienteReferente: null,
            nombreClienteReferente: '',
            nombreReferenteExterno: nombre,
            referencia: REFERENCIA_OTRO_CLIENTE,
            idReferencia: null,
        });
        limpiarBusqueda();
    };

    const handleQuitarReferente = () => {
        onChange({ cuilClienteReferente: null, nombreClienteReferente: '', nombreReferenteExterno: null });
        setReferenteSinCatalogo(null);
        setMensajeAsignacion(null);
        limpiarBusqueda();
    };

    // Aviso + asignación cuando el referente no tiene referencia del catálogo.
    const renderReferenteSinCatalogo = () => {
        if (!referenteSinCatalogo || referenteSinCatalogo.cuil !== value?.cuilClienteReferente) return null;
        const opciones = [...referenciasDisponibles].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        return (
            <div className="mt-2 p-2 rounded" style={{ background: '#fffbeb', border: '1px solid #fcd34d', fontSize: '0.85rem' }}>
                <div style={{ color: '#92400e' }}>
                    <i className="bi bi-exclamation-triangle-fill me-1" />
                    <strong>{referenteSinCatalogo.nombre}</strong> no tiene una referencia del catálogo
                    {referenteSinCatalogo.textoLibre ? <> (tiene anotado: «{referenteSinCatalogo.textoLibre}»)</> : null}.
                    {' '}Si no se la asignás, este cliente va a quedar como <strong>{REFERENCIA_OTRO_CLIENTE}</strong>.
                </div>
                <div className="d-flex gap-2 mt-2">
                    <select
                        className="form-select form-select-sm"
                        aria-label="Referencia para el referente"
                        value={idReferenciaParaReferente}
                        onChange={(e) => setIdReferenciaParaReferente(e.target.value)}
                    >
                        <option value="">Elegí la referencia de {referenteSinCatalogo.nombre}…</option>
                        {opciones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                    </select>
                    <button
                        type="button"
                        className="btn btn-sm btn-warning flex-shrink-0"
                        disabled={!idReferenciaParaReferente || asignando}
                        onClick={handleAsignarAlReferente}
                    >
                        {asignando ? 'Asignando…' : 'Asignar'}
                    </button>
                </div>
            </div>
        );
    };

    // Tarjeta del referente ya elegido, sea cliente del estudio o nombre suelto.
    const renderReferenteElegido = () => {
        const esInterno = Boolean(value?.cuilClienteReferente);
        return (
            <div className="p-2 rounded" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                <div className="d-flex justify-content-between align-items-center gap-3">
                    {/* min-w-0 permite que un nombre largo se recorte en vez de empujar al botón */}
                    <div style={{ minWidth: 0 }}>
                        <i className={`bi ${esInterno ? 'bi-person-check-fill' : 'bi-person-plus-fill'} me-2 text-primary`} />
                        <strong>{esInterno ? value.nombreClienteReferente : value.nombreReferenteExterno}</strong>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary flex-shrink-0"
                        onClick={handleQuitarReferente}
                    >
                        Cambiar
                    </button>
                </div>
                <div className="mt-1" style={{ fontSize: '0.85rem', color: '#0369a1' }}>
                    {esInterno ? (
                        <>Referencia heredada: {value.referencia || 'sin referencia asignada'}</>
                    ) : (
                        <>Cliente no registrado en el sistema | Referencia: {REFERENCIA_OTRO_CLIENTE}</>
                    )}
                </div>
                {esInterno && renderReferenteSinCatalogo()}
                {mensajeAsignacion && (
                    <div className="mt-1" style={{ fontSize: '0.85rem', color: mensajeAsignacion.tipo === 'ok' ? '#15803d' : '#b91c1c' }}>
                        {mensajeAsignacion.texto}
                    </div>
                )}
            </div>
        );
    };

    // Alta a mano del referente que no está cargado en el sistema.
    const renderCargaManual = () => (
        <div>
            <div className="input-group">
                <span className="input-group-text bg-light">
                    <i className="bi bi-person-plus" />
                </span>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Nombre y apellido de quien lo trajo"
                    value={nombreManual}
                    onChange={(e) => setNombreManual(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleConfirmarManual();
                        }
                    }}
                />
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirmarManual}
                    disabled={!nombreManual.trim()}
                >
                    Confirmar
                </button>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1">
                <small className="text-muted">
                    Se guardará como <strong>{REFERENCIA_OTRO_CLIENTE}</strong>: no hay referencia de origen que heredar.
                </small>
                <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-decoration-none flex-shrink-0 ms-2"
                    onClick={() => { setModoManual(false); setNombreManual(''); }}
                >
                    Volver a buscar
                </button>
            </div>
        </div>
    );

    // Buscador de clientes ya cargados.
    const renderBuscador = () => (
        <div className="position-relative">
            <div className="input-group">
                <span className="input-group-text bg-light">
                    <i className="bi bi-search" />
                </span>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar cliente por nombre, apellido o CUIL..."
                    value={criterio}
                    onChange={(e) => setCriterio(e.target.value)}
                />
            </div>
            {buscando && <small className="text-muted d-block mt-1">Buscando…</small>}
            {resultados.length > 0 && (
                // En flujo normal (no position:absolute) con scroll propio: dentro de un modal
                // que ya scrollea, un overlay absoluto queda recortado por el borde del modal y
                // obliga a scrollear todo el formulario para ver el resto de los resultados.
                <div
                    className="list-group shadow-sm"
                    style={{ marginTop: 2, maxHeight: 240, overflowY: 'auto' }}
                >
                    {resultados.slice(0, MAX_RESULTADOS_VISIBLES).map((c) => (
                        <button
                            type="button"
                            key={c.cuil}
                            className="list-group-item list-group-item-action"
                            onClick={() => handleSeleccionarReferente(c)}
                        >
                            <strong>{apellidoNombre(c.apellido, c.nombre)}</strong>
                            {c.referencia && <span className="text-muted ms-2">— {c.referencia}</span>}
                        </button>
                    ))}
                    {resultados.length > MAX_RESULTADOS_VISIBLES && (
                        <div className="list-group-item text-muted text-center" style={{ fontSize: '0.8rem' }}>
                            +{resultados.length - MAX_RESULTADOS_VISIBLES} más — seguí escribiendo para afinar la búsqueda
                        </div>
                    )}
                </div>
            )}
            {!buscando && criterio.trim() && resultados.length === 0 && (
                <small className="text-muted d-block mt-1">No se encontraron clientes.</small>
            )}
            {/* La mayoría de los referentes todavía no están cargados como clientes. */}
            <button
                type="button"
                className="btn btn-link btn-sm p-0 mt-1 text-decoration-none"
                onClick={() => { setModoManual(true); setNombreManual(criterio.trim().toUpperCase()); }}
            >
                <i className="bi bi-pencil me-1" />
                No está en el sistema, escribir el nombre
            </button>
        </div>
    );

    return (
        <div>
            <div className="form-check form-switch mb-2">
                <input
                    className="form-check-input"
                    type="checkbox"
                    id="switchOtroClienteReferente"
                    checked={modoOtroCliente}
                    onChange={(e) => handleToggle(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="switchOtroClienteReferente" style={{ fontSize: '0.9rem' }}>
                    Fue traído por otro cliente del estudio
                </label>
            </div>

            {modoOtroCliente ? (
                hayReferente ? renderReferenteElegido()
                    : modoManual ? renderCargaManual()
                        : renderBuscador()
            ) : (
                <ComboboxDatosMaestros
                    label="Referencia / Etiqueta"
                    placeholder="Seleccione o cree una etiqueta..."
                    opciones={referenciasDisponibles}
                    valor={value?.referencia || ''}
                    onChange={(nuevoNombre) => {
                        const encontrada = referenciasDisponibles.find(
                            (r) => r.nombre?.toLowerCase() === nuevoNombre?.toLowerCase()
                        );
                        onChange({ referencia: nuevoNombre, idReferencia: encontrada ? encontrada.id : null });
                    }}
                    onCrearNuevo={onCrearNuevaReferencia}
                    tipo="referencia"
                    icon={icon}
                />
            )}
        </div>
    );
}
